import { Box, IconButton, InputAdornment, Grid } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { Controller } from 'react-hook-form';
import { TFormField } from '@models/forms';
import { useFormStore } from '@components/forms';
import { TextField } from '@components/textField/TextField';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { useState } from 'react';
import { PasswordHelperGuide } from '@pages/Login/SignUpView/PasswordHelperGuide';
import { EyeStrikeThrough, Eye } from '@assets/svgs';

export type CreatePasswordFieldProps = {
  field: TFormField;
  forceDisabled: boolean;
  isRequired: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
};

export const CreatePasswordField = ({ field, disabled, required, error, helperText }: CreatePasswordFieldProps) => {
  const { control, getValues } = useFormStore((state) => state);
  const [showPassword, setShowPassword] = useState(false);
  const [typingHasStopped, setTypingHasStopped] = useState(false);
  const [value, setValue] = useState('');

  const validator = {
    required,
  };

  const passwordCompleteRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

  const min8CharsRegex = /.{8,}/;
  const containsANumberRegex = /(?=.*[0-9])/;
  const containsLowerAndUppercaseRegex = /(?=.*[a-z])(?=.*[A-Z])/;
  const containsASymbolRegex = /(?=.*[!@#$%^&*])/;

  function doesPasswordContainEmailSubset(password: string): boolean {
    const email = getValues('email');

    if (!email) return false;
    const emailPart = email;
    if (emailPart.length < 4) return false;
    for (let i = 0; i <= emailPart.length - 4; i++) {
      const subset = emailPart.substring(i, i + 4);
      if (subset && password.toLowerCase().includes(subset.toLowerCase())) {
        return true;
      }
    }
    return false;
  }

  const passwordGuide = [
    { text: 'At least 8 characters', regex: min8CharsRegex },
    { text: 'Contains a number', regex: containsANumberRegex },
    { text: 'Contains a symbol !@#$%^&*', regex: containsASymbolRegex },
    { text: 'Contains a lowercase and uppercase letter', regex: containsLowerAndUppercaseRegex },
    {
      text: 'Does not contain part of email',
      onChange: (password: string) => !doesPasswordContainEmailSubset(password),
    },
  ];

  const valid = passwordCompleteRegex.test(value) && !doesPasswordContainEmailSubset(value);
  const hasError = !!error || (!valid && typingHasStopped);

  const handleDebouncedError = useDebouncedCallback(() => {
    setTypingHasStopped(true);
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  return (
    <Controller
      rules={{
        ...validator,
        pattern: passwordCompleteRegex,
        validate: {
          doesNotContainEmail: (value: string) => {
            if (doesPasswordContainEmailSubset(value)) {
              return true;
            }
            return true;
          },
        },
      }}
      control={control}
      name={field.name}
      defaultValue={field.defaultValue || ''}
      render={({ field: { onChange, value } }) => {
        return (
          <Box>
            <TextField
              fullWidth
              value={value}
              data-testid={`text-field-${field.name}`}
              inputLabel={field.label}
              required={required}
              placeholder={field.placeholder}
              helperText={helperText}
              error={hasError}
              type={showPassword ? 'text' : 'password'}
              autoFocus={field.autoFocus}
              disabled={disabled}
              name={field.name}
              autoComplete='new-password'
              onChange={(event) => {
                setValue(event.target.value);
                onChange(event);
                handleDebouncedError();
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end' sx={{ height: 20 }}>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={() => setShowPassword((cur) => !cur)}
                        edge='end'
                      >
                        {showPassword ? <EyeStrikeThrough color='white' /> : <Eye color='white' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Grid
              sx={{
                mt: 1,
                textAlign: 'left',
              }}
            >
              {passwordGuide.map((guide) => (
                <PasswordHelperGuide
                  key={guide.text}
                  isDirty={typingHasStopped}
                  regex={guide.regex}
                  onChange={guide.onChange}
                  text={guide.text}
                  value={value}
                />
              ))}
            </Grid>
          </Box>
        );
      }}
    />
  );
};
